import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import type {
  Address,
  CurrentUser,
  LastLocationResponse,
  ResolveZoneResponse,
  ZoneBrief,
} from '@arbuz/shared';
import type { LatLon } from '@arbuz/ui-map';
import { ApiError, api } from '../api';
import { formatMoney, makeT } from '../i18n';
import {
  haptic,
  isLocationAvailable,
  openLocationSettings,
  openTelegramLink,
  requestLocation,
  type LocationFallbackReason,
} from '../tg';

/**
 * Карта весит заметно больше остального экрана, а нужна не всем: половина
 * пользователей отдаст геолокацию одним тапом. Грузим по требованию.
 */
const MapPicker = lazy(async () => {
  await import('@arbuz/ui-map/styles.css');
  const m = await import('@arbuz/ui-map');
  return { default: m.MapPicker };
});

type Mode = 'idle' | 'map';

export function AddressScreen({
  user,
  onSaved,
}: {
  user: CurrentUser;
  onSaved: (a: Address) => void;
}) {
  const t = makeT(user.locale);

  const [point, setPoint] = useState<LatLon | null>(null);
  const [source, setSource] = useState<'telegram' | 'map' | 'manual'>('manual');
  const [mode, setMode] = useState<Mode>('idle');
  const [zone, setZone] = useState<ZoneBrief | null>(null);
  const [covered, setCovered] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);

  /**
   * Причина, по которой геолокация Telegram не сработала. Раньше отказ был
   * неотличим от ошибки: экран молча переключался на карту без единого
   * слова объяснения.
   */
  const [locationIssue, setLocationIssue] = useState<LocationFallbackReason | null>(null);

  /** Точка, присланная боту в чате — второй канал геолокации из ADR-0003. */
  const [lastLocation, setLastLocation] = useState<LastLocationResponse>(null);
  const [lastLocationDismissed, setLastLocationDismissed] = useState(false);

  const [form, setForm] = useState({
    address_text: '',
    house: '',
    entrance: '',
    floor: '',
    apartment: '',
    landmark: '',
  });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  /** Зона определяется по координатам — без них нельзя посчитать доставку. */
  const checkZone = useCallback(async (p: LatLon) => {
    setChecking(true);
    try {
      const res = await api.post<ResolveZoneResponse>('/api/addresses/resolve-zone', {
        lat: p.lat,
        lon: p.lon,
      });
      setCovered(res.covered);
      setZone(res.zone);
      haptic(res.covered ? 'success' : 'warning');
    } catch {
      setCovered(null);
      setZone(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (point) void checkZone(point);
  }, [point, checkZone]);

  /**
   * Точка из чата бота подхватывается один раз при открытии экрана.
   * Автоматически не подставляется: пользователь мог уже выбрать что-то
   * на карте, и молчаливая подмена была бы хуже отсутствия функции.
   */
  useEffect(() => {
    api
      .get<LastLocationResponse>('/api/addresses/last-location')
      .then((loc) => {
        if (!loc) return;
        const ageMs = Date.now() - new Date(loc.received_at).getTime();
        // Старше десяти минут — не то, что человек прислал только что
        if (ageMs >= 0 && ageMs < 10 * 60 * 1000) setLastLocation(loc);
      })
      .catch(() => {
        // Баннер необязателен — молча пропускаем, экран работает и без него
      });
  }, []);

  function useTelegramLocation() {
    haptic('light');
    setLocationIssue(null);
    requestLocation(
      (p) => {
        setSource('telegram');
        setPoint(p);
      },
      (reason) => {
        // Фолбэк обязателен: LocationManager есть не у всех, и в доступе
        // могут отказать. Без него часть людей не оформит заказ вообще.
        setLocationIssue(reason);
        setSource('map');
        setMode('map');
        haptic('warning');
      },
    );
  }

  function useLastLocation(): void {
    if (!lastLocation) return;
    haptic('light');
    setSource('telegram');
    setPoint({ lat: lastLocation.lat, lon: lastLocation.lon });
    setLastLocationDismissed(true);
  }

  async function save() {
    if (!point) return;
    setSaving(true);
    setFields({});

    try {
      const created = await api.post<Address>('/api/addresses', {
        lat: point.lat,
        lon: point.lon,
        source,
        address_text: form.address_text,
        house: form.house || null,
        entrance: form.entrance || null,
        floor: form.floor || null,
        apartment: form.apartment || null,
        landmark: form.landmark || null,
        is_default: true,
      });
      haptic('success');
      onSaved(created);
    } catch (err) {
      haptic('error');
      setFields(err instanceof ApiError ? (err.fields ?? { _: err.message }) : {});
    } finally {
      setSaving(false);
    }
  }

  const canSave = point !== null && covered === true && form.address_text.trim().length > 0;

  return (
    <main className="page" style={{ paddingBottom: 120 }}>
      <h1 className="h1">{t('addressTitle')}</h1>

      {/* Точка из чата бота — подтверждается явно, не подставляется молча */}
      {lastLocation && !lastLocationDismissed && !point && (
        <div
          className="card"
          role="status"
          style={{ marginBottom: 'var(--s-3)', background: 'var(--c-primary-subtle)' }}
        >
          <p style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>{t('lastLocationBanner')}</p>
          <div style={{ display: 'flex', gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
            <button
              type="button"
              className="btn-primary"
              style={{ flex: 1 }}
              onClick={useLastLocation}
            >
              {t('lastLocationUse')}
            </button>
            <button
              type="button"
              onClick={() => setLastLocationDismissed(true)}
              style={{ ...secondaryButton, flex: 1 }}
            >
              {t('dismiss')}
            </button>
          </div>
        </div>
      )}

      {/* Способ 1: геолокация Telegram — один тап, самый точный путь */}
      {isLocationAvailable() && (
        <button
          type="button"
          className="btn-primary"
          style={{ width: '100%', marginBottom: 'var(--s-3)' }}
          onClick={useTelegramLocation}
        >
          {t('useMyLocation')}
        </button>
      )}

      {/* Ошибка геолокации: раньше здесь было тихо, теперь — причина и действие */}
      {locationIssue && (
        <div
          className="card"
          role="alert"
          style={{ marginBottom: 'var(--s-3)', background: 'var(--c-highlight)' }}
        >
          <p style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
            {locationIssue === 'denied' && t('locationDenied')}
            {(locationIssue === 'unsupported' || locationIssue === 'unavailable') &&
              t('locationUnsupported')}
            {locationIssue === 'error' && t('locationError')}
          </p>

          <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap', marginTop: 'var(--s-3)' }}>
            {locationIssue === 'denied' && (
              <button
                type="button"
                onClick={() => {
                  haptic('light');
                  openLocationSettings();
                }}
                style={secondaryButton}
              >
                {t('openLocationSettings')}
              </button>
            )}

            {(locationIssue === 'denied' || locationIssue === 'error') && (
              <button type="button" onClick={useTelegramLocation} style={secondaryButton}>
                {t('retryLocation')}
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                haptic('light');
                openTelegramLink('https://t.me/hochuarbuz_bot?start=loc');
              }}
              style={secondaryButton}
            >
              {t('sendLocationViaBot')}
            </button>
          </div>
        </div>
      )}

      {/* Способ 2: точка на карте — работает всегда */}
      <button
        type="button"
        onClick={() => {
          haptic('light');
          setSource('map');
          setMode(mode === 'map' ? 'idle' : 'map');
        }}
        style={secondaryButton}
      >
        {mode === 'map' ? t('hideMap') : t('pickOnMap')}
      </button>

      {mode === 'map' && (
        <div style={{ marginTop: 'var(--s-3)' }}>
          <Suspense fallback={<p className="muted">{t('mapLoading')}</p>}>
            <MapPicker
              value={point}
              onChange={(p) => {
                setSource('map');
                setPoint(p);
              }}
            />
          </Suspense>
        </div>
      )}

      {/* Итог по зоне: клиент должен видеть цену доставки до оформления */}
      <div className="card" style={{ marginTop: 'var(--s-4)' }} aria-live="polite">
        {!point ? (
          <p className="muted" style={{ margin: 0 }}>
            {t('pointNotSet')}
          </p>
        ) : checking ? (
          <p className="muted" style={{ margin: 0 }}>
            {t('checkingZone')}
          </p>
        ) : covered && zone ? (
          <>
            <p style={{ margin: 0, fontWeight: 'var(--fw-semibold)' }}>{zone.name}</p>
            <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-sm)' }}>
              {t('deliveryPrice')} {formatMoney(zone.delivery_price, user.locale)} {t('sum')}
            </p>
          </>
        ) : (
          <p style={{ margin: 0, color: 'var(--c-danger)' }} role="alert">
            {t('outOfZone')}
          </p>
        )}
      </div>

      {/* Способ 3: текстовые детали — их нет ни в каких координатах */}
      <div style={{ marginTop: 'var(--s-4)', display: 'grid', gap: 'var(--s-3)' }}>
        <Field id="address_text" label={t('streetLabel')} value={form.address_text}
          error={fields.address_text} onChange={(v) => setForm({ ...form, address_text: v })} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-3)' }}>
          <Field id="house" label={t('houseLabel')} value={form.house}
            onChange={(v) => setForm({ ...form, house: v })} />
          <Field id="apartment" label={t('apartmentLabel')} value={form.apartment}
            onChange={(v) => setForm({ ...form, apartment: v })} />
          <Field id="entrance" label={t('entranceLabel')} value={form.entrance}
            onChange={(v) => setForm({ ...form, entrance: v })} />
          <Field id="floor" label={t('floorLabel')} value={form.floor}
            onChange={(v) => setForm({ ...form, floor: v })} />
        </div>
        <Field id="landmark" label={t('landmarkLabel')} value={form.landmark}
          onChange={(v) => setForm({ ...form, landmark: v })} />
      </div>

      {fields._ && (
        <p role="alert" style={{ color: 'var(--c-danger)', fontSize: 'var(--fs-sm)' }}>
          {fields._}
        </p>
      )}

      <div style={bottomBar}>
        <button
          type="button"
          className="btn-primary"
          style={{ width: '100%' }}
          disabled={!canSave || saving}
          onClick={() => void save()}
        >
          {saving ? t('saving') : t('saveAddress')}
        </button>
      </div>
    </main>
  );
}

function Field({
  id, label, value, onChange, error,
}: {
  id: string; label: string; value: string;
  onChange: (v: string) => void; error?: string;
}) {
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', marginBottom: 'var(--s-2)', fontSize: 'var(--fs-sm)', color: 'var(--c-text-secondary)' }}>
        {label}
      </label>
      <input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        style={{
          width: '100%',
          minHeight: 'var(--tap-min)',
          padding: '0 var(--s-3)',
          borderRadius: 'var(--r-md)',
          border: `1px solid ${error ? 'var(--c-danger)' : 'var(--c-border-input)'}`,
          background: 'var(--c-surface)',
          color: 'var(--c-text)',
          font: 'var(--fw-regular) var(--fs-body)/var(--lh-body) var(--font)',
        }}
      />
      {error && (
        <div id={`${id}-err`} role="alert" style={{ color: 'var(--c-danger)', fontSize: 'var(--fs-sm)', marginTop: 'var(--s-1)' }}>
          {error}
        </div>
      )}
    </div>
  );
}

const secondaryButton: React.CSSProperties = {
  width: '100%',
  minHeight: 'var(--tap-min)',
  borderRadius: 'var(--r-lg)',
  border: '1px solid var(--c-border-input)',
  background: 'var(--c-surface)',
  color: 'var(--c-primary-text)',
  font: 'var(--fw-semibold) var(--fs-body)/var(--lh-body) var(--font)',
  cursor: 'pointer',
};

const bottomBar: React.CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  padding: 'var(--s-3) var(--page-x)',
  paddingBottom: 'calc(var(--s-3) + var(--tg-safe-bottom))',
  background: 'var(--c-surface)',
  borderTop: '1px solid var(--c-border)',
  boxShadow: 'var(--sh-lg)',
};
